import produce from 'immer';
import {
  types,
  HardwareState,
  HardwareActionTypes,
  RegisterKioskPrinters,
  PingPrintersRequest,
  PingOnePrinterPending,
  PingOnePrinterSuccess,
  PingOnePrinterError,
  PrintOneJobSuccess,
  PrintOneJobError,
  SetPrintData,
  PrintAllJobsPending,
  ConfigurePrinterRequest,
  ConfigurePrinterSuccess,
  ConfigurePrinterError,
  ResetPrinterConfiguredStatus,
  ScannerPingSuccess,
  ScannerPingError,
  ScannerPingPending,
  EventDataPingSuccess,
  EventDataPingError,
  EventDataPingPending,
  PaymentPingSuccess,
  PaymentPingError,
  PaymentPingPending,
  PaymentSaleSuccess,
  PaymentSalePending,
  ScannerReadSuccess,
  ScannerReadError,
  PaymentVoidError,
  PaymentVoidRequest,
  PaymentVoidPending,
  PaymentCancelError,
  PaymentCancelPending,
  CameraSnapError,
  CameraSnapSuccess
} from './types';
import { UNKNOWN } from 'constants/hardware';
import { PaymentSaleError } from '.';

// Actions
export const actions = {
  setPrintData: (payload: SetPrintData['payload']): HardwareActionTypes => ({ type: types.SET_PRINT_DATA, payload }),
  printAllJobs: (): HardwareActionTypes => ({ type: types.PRINT_ALL_JOBS_ASYNC.REQUEST }),
  pingPrinters: (payload: PingPrintersRequest['payload']): HardwareActionTypes => ({
    type: types.PING_PRINTERS_ASYNC.REQUEST,
    payload
  }),
  scannerPing: (userInitiated: boolean): HardwareActionTypes => ({
    type: types.SCANNER_PING_ASYNC.REQUEST,
    payload: { userInitiated }
  }),
  paymentPing: (userInitiated: boolean): HardwareActionTypes => ({
    type: types.PAYMENT_PING_ASYNC.REQUEST,
    payload: { userInitiated }
  }),
  paymentSale: () => ({ type: types.PAYMENT_SALE_ASYNC.REQUEST }),
  paymentVoid: (payload: PaymentVoidRequest['payload']) => ({ type: types.PAYMENT_VOID_ASYNC.REQUEST, payload }),
  paymentCancel: () => ({ type: types.PAYMENT_CANCEL_ASYNC.REQUEST }),
  eventDataPing: (): HardwareActionTypes => ({ type: types.EVENT_DATA_PING_ASYNC.REQUEST }),
  scannerRead: (): HardwareActionTypes => ({ type: types.SCANNER_READ_ASYNC.REQUEST }),
  snapPicture: (): HardwareActionTypes => ({ type: types.CAMERA_SNAP_ASYNC.REQUEST }),
  resetScanner: (): HardwareActionTypes => ({ type: types.RESET_SCANNER }),
  configurePrinter: (payload: ConfigurePrinterRequest['payload']) => ({
    type: types.CONFIGURE_PRINTER_ASYNC.REQUEST,
    payload
  }),
  resetPrinterConfiguredStatus: (payload: ResetPrinterConfiguredStatus['payload']) => ({
    type: types.RESET_PRINTER_CONFIGURED_STATUS,
    payload
  }),
  resetSaleData: () => ({ type: types.RESET_SALE_DATA }),
  resetCancelError: () => ({ type: types.RESET_CANCEL_ERROR }),
  logToLocalServer: (log: string): HardwareActionTypes => ({
    type: types.LOG_TO_LOCAL_SERVER,
    payload: log
  })
};

// Initial State
export const initialState: HardwareState = {
  eventData: {
    status: {
      rawCode: '',
      message: '',
      code: UNKNOWN
    },
    loading: false
  },
  scanner: {
    status: {
      rawCode: '',
      message: '',
      code: UNKNOWN
    },
    loading: false
  },
  camera: {
    status: {
      rawCode: '',
      message: '',
      code: UNKNOWN
    },
    loading: false
  },
  paymentDevice: {
    status: {
      rawCode: '',
      message: '',
      code: UNKNOWN
    },
    saleData: null,
    loading: false,
    cancelLoading: false,
    cancelSuccess: false,
    cancelError: null,
    voidError: null
  },
  printers: [],
  printData: {
    total: 0,
    count: 0,
    printJobs: [],
    loading: false
  }
};

// Reducer
export const reducer = (state: HardwareState = initialState, action: HardwareActionTypes): HardwareState => {
  return produce(state, (draft) => {
    let payload: any;
    switch (action.type) {
      case types.REGISTER_KIOSK_PRINTERS:
        draft.printers = (action as RegisterKioskPrinters).payload.map((kioskHardwareId) => ({
          kioskHardwareId,
          status: {
            rawCode: '',
            message: '',
            code: UNKNOWN
          },
          loading: false,
          configuredStatus: {
            completed: false,
            pending: false
          }
        }));
        break;
      case types.CONFIGURE_PRINTER_ASYNC.PENDING:
        payload = (action as ConfigurePrinterSuccess).payload;
        draft.printers.forEach((p) => {
          if (p.kioskHardwareId === payload.kioskHardwareId) {
            p.configuredStatus.pending = payload.pending;
          }
        });
        break;
      case types.CONFIGURE_PRINTER_ASYNC.SUCCESS:
        payload = (action as ConfigurePrinterSuccess).payload;
        draft.printers.forEach((p) => {
          if (p.kioskHardwareId === payload.kioskHardwareId) {
            p.configuredStatus.completed = true;
          }
        });
        break;
      case types.CONFIGURE_PRINTER_ASYNC.ERROR:
        payload = (action as ConfigurePrinterError).payload;
        draft.printers.forEach((p) => {
          if (p.kioskHardwareId === payload.kioskHardwareId) {
            p.configuredStatus.error = payload.message;
          }
        });
        break;
      case types.RESET_PRINTER_CONFIGURED_STATUS:
        payload = (action as ResetPrinterConfiguredStatus).payload;
        draft.printers.forEach((p) => {
          if (p.kioskHardwareId === payload.kioskHardwareId) {
            p.configuredStatus = {
              completed: false,
              pending: false
            };
          }
        });
        break;
      case types.PING_ONE_PRINTER_ASYNC.PENDING:
        payload = (action as PingOnePrinterPending).payload;
        draft.printers.forEach((p) => {
          if (p.kioskHardwareId === payload.kioskHardwareId) {
            p.loading = payload.pending;
          }
        });
        break;
      case types.PING_ONE_PRINTER_ASYNC.SUCCESS:
      case types.PING_ONE_PRINTER_ASYNC.ERROR:
      case types.PRINT_ONE_JOB_ASYNC.ERROR:
        payload = (action as PingOnePrinterSuccess | PingOnePrinterError | PrintOneJobError).payload;
        draft.printers.forEach((p) => {
          if (p.kioskHardwareId === payload.kioskHardwareId) {
            p.status = payload.status;
          }
        });
        break;
      case types.SET_PRINT_DATA:
        draft.printData = {
          printJobs: (action as SetPrintData).payload.printJobs,
          total: (action as SetPrintData).payload.printJobs.length,
          count: 0,
          loading: false,
          isReprint: (action as SetPrintData).payload.isReprint
        };
        break;
      case types.PRINT_ALL_JOBS_ASYNC.PENDING:
        draft.printData.loading = (action as PrintAllJobsPending).payload;
        break;
      case types.PRINT_ONE_JOB_ASYNC.SUCCESS:
        payload = (action as PrintOneJobSuccess).payload;
        draft.printers.forEach((p) => {
          if (p.kioskHardwareId === payload.kioskHardwareId) {
            p.status = payload.status;
          }
        });
        draft.printData.printJobs[payload.jobIndex].printed = true;
        draft.printData.count = (draft.printData.count || 0) + 1;
        break;
      case types.SCANNER_PING_ASYNC.SUCCESS:
      case types.SCANNER_PING_ASYNC.ERROR:
      case types.SCANNER_READ_ASYNC.SUCCESS:
      case types.SCANNER_READ_ASYNC.ERROR:
        draft.scanner.status = (
          action as ScannerPingSuccess | ScannerPingError | ScannerReadSuccess | ScannerReadError
        ).payload;
        break;
      case types.RESET_SCANNER:
        draft.scanner.status = { ...initialState.scanner.status, code: draft.scanner.status.code };
        break;
      case types.SCANNER_PING_ASYNC.PENDING:
      case types.SCANNER_READ_ASYNC.PENDING:
        draft.scanner.loading = (action as ScannerPingPending).payload;
        break;
      case types.PAYMENT_PING_ASYNC.SUCCESS:
      case types.PAYMENT_PING_ASYNC.ERROR:
      case types.PAYMENT_SALE_ASYNC.ERROR:
        draft.paymentDevice.status = (action as PaymentPingSuccess | PaymentPingError | PaymentSaleError).payload;
        break;
      case types.PAYMENT_SALE_ASYNC.SUCCESS:
        draft.paymentDevice.status = (action as PaymentSaleSuccess).payload.status;
        draft.paymentDevice.saleData = (action as PaymentSaleSuccess).payload.saleData;
        break;
      case types.PAYMENT_PING_ASYNC.PENDING:
      case types.PAYMENT_SALE_ASYNC.PENDING:
      case types.PAYMENT_VOID_ASYNC.PENDING:
        draft.paymentDevice.loading = (action as PaymentPingPending | PaymentSalePending | PaymentVoidPending).payload;
        break;
      case types.PAYMENT_VOID_ASYNC.ERROR:
        draft.paymentDevice.voidError = (action as PaymentVoidError).payload;
        break;
      case types.PAYMENT_VOID_ASYNC.SUCCESS:
        draft.paymentDevice.voidError = null;
        break;
      case types.EVENT_DATA_PING_ASYNC.SUCCESS:
      case types.EVENT_DATA_PING_ASYNC.ERROR:
        draft.eventData.status = (action as EventDataPingSuccess | EventDataPingError).payload;
        break;
      case types.EVENT_DATA_PING_ASYNC.PENDING:
        draft.eventData.loading = (action as EventDataPingPending).payload;
        break;
      case types.RESET_SALE_DATA:
        draft.paymentDevice.saleData = null;
        break;
      case types.CAMERA_SNAP_ASYNC.SUCCESS:
      case types.CAMERA_SNAP_ASYNC.ERROR:
        draft.camera.status = (action as CameraSnapError | CameraSnapSuccess).payload;
        break;
      case types.PAYMENT_CANCEL_ASYNC.PENDING:
        draft.paymentDevice.cancelLoading = (action as PaymentCancelPending).payload;
        break;
      case types.PAYMENT_CANCEL_ASYNC.SUCCESS:
        draft.paymentDevice.cancelSuccess = true;
        break;
      case types.PAYMENT_CANCEL_ASYNC.ERROR:
        draft.paymentDevice.cancelError = (action as PaymentCancelError).payload;
        break;
      case types.RESET_CANCEL_ERROR:
        draft.paymentDevice.cancelError = null;
        draft.paymentDevice.cancelSuccess = false;
        break;
      default:
    }
  });
};
